// Página AdminSuppliesList - Gestión de insumos
// Lista y gestiona todos los insumos del sistema

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useSupplies } from '../../hooks/useSupplies'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import { formatCurrency } from '../../utils/helpers'
import './AdminSuppliesList.css'

function AdminSuppliesList() {
  const { supplies, loading, error, refetch } = useSupplies(true)
  const [filteredSupplies, setFilteredSupplies] = useState([])
  const [notification, setNotification] = useState(null)

  const [categoryFilter, setCategoryFilter] = useState('all')
  const [supplierFilter] = useState('all')
  const [activeFilter, setActiveFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  const applyFilters = useCallback(() => {
    let filtered = [...(supplies || [])]

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(s => s.category === categoryFilter)
    }

    if (supplierFilter !== 'all') {
      filtered = filtered.filter(s => 
        (s.supplierIds || []).includes(supplierFilter)
      )
    }

    if (activeFilter !== 'all') {
      filtered = filtered.filter(s => 
        activeFilter === 'active' ? s.active !== false : s.active === false
      )
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(term) ||
        s.sku?.toLowerCase().includes(term)
      )
    }

    setFilteredSupplies(filtered)
  }, [supplies, categoryFilter, supplierFilter, activeFilter, searchTerm])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const categories = ['herramientas', 'materiales', 'quimicos', 'otros']

  if (loading) {
    return (
      <div className="admin-supplies-list-page">
        <div className="loading-state">
          <p>Cargando insumos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-supplies-list-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={refetch}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-supplies-list-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="supplies-header">
        <div>
          <h1>Gestión de Insumos</h1>
          <p className="supplies-subtitle">
            Administra los insumos y materiales del negocio
          </p>
        </div>
        <Link to="/admin/insumos/nuevo">
          <Button variant="primary">
            ➕ Nuevo Insumo
          </Button>
        </Link>
      </div>

      <div className="supplies-filters">
        <div className="filter-group">
          <label htmlFor="categoryFilter">Categoría:</label>
          <select
            id="categoryFilter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">Todas</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="activeFilter">Estado:</label>
          <select
            id="activeFilter"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Nombre, SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Vista de tabla - Desktop */}
      <div className="supplies-table-container">
        {filteredSupplies.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron insumos con los filtros aplicados</p>
            {!searchTerm && categoryFilter === 'all' && activeFilter === 'all' && (
              <Link to="/admin/insumos/nuevo">
                <Button variant="primary" style={{ marginTop: '1rem' }}>
                  ➕ Crear Primer Insumo
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <table className="supplies-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>SKU</th>
                <th>Categoría</th>
                <th>Unidad</th>
                <th>Último Precio</th>
                <th>Stock</th>
                <th>Proveedores</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredSupplies.map((supply) => (
                <tr key={supply.id} className={supply.active === false ? 'inactive-row' : ''}>
                  <td><strong>{supply.name}</strong></td>
                  <td>{supply.sku}</td>
                  <td>{supply.category}</td>
                  <td>{supply.unit}</td>
                  <td>
                    {supply.lastPurchasePrice 
                      ? formatCurrency(supply.lastPurchasePrice)
                      : '-'}
                  </td>
                  <td>
                    {supply.trackStock 
                      ? `${supply.currentStock || 0} ${supply.unit}`
                      : 'No rastreado'}
                  </td>
                  <td>
                    {(supply.supplierIds || []).length > 0
                      ? `${supply.supplierIds.length} proveedor${supply.supplierIds.length !== 1 ? 'es' : ''}`
                      : '-'}
                  </td>
                  <td>
                    <span className={`status-badge ${supply.active !== false ? 'active' : 'inactive'}`}>
                      {supply.active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <Link to={`/admin/insumos/${supply.id}`}>
                      <Button variant="outline" size="small">
                        Editar
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Vista de cards - Mobile */}
      <div className="supplies-cards-container">
        {filteredSupplies.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron insumos con los filtros aplicados</p>
            {!searchTerm && categoryFilter === 'all' && activeFilter === 'all' && (
              <Link to="/admin/insumos/nuevo">
                <Button variant="primary" style={{ marginTop: '1rem' }}>
                  ➕ Crear Primer Insumo
                </Button>
              </Link>
            )}
          </div>
        ) : (
          filteredSupplies.map((supply) => (
            <div key={supply.id} className={`supply-card ${supply.active === false ? 'inactive' : ''}`}>
              <div className="supply-card-header">
                <div>
                  <h3>{supply.name}</h3>
                  <span className="supply-sku">{supply.sku}</span>
                </div>
                <span className={`status-badge ${supply.active !== false ? 'active' : 'inactive'}`}>
                  {supply.active !== false ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="supply-card-body">
                <div className="supply-info-row">
                  <span className="info-label">Categoría:</span>
                  <span className="info-value">{supply.category}</span>
                </div>
                <div className="supply-info-row">
                  <span className="info-label">Unidad:</span>
                  <span className="info-value">{supply.unit}</span>
                </div>
                {supply.lastPurchasePrice && (
                  <div className="supply-info-row">
                    <span className="info-label">Último Precio:</span>
                    <span className="info-value">{formatCurrency(supply.lastPurchasePrice)}</span>
                  </div>
                )}
                <div className="supply-info-row">
                  <span className="info-label">Stock:</span>
                  <span className="info-value">
                    {supply.trackStock 
                      ? `${supply.currentStock || 0} ${supply.unit}`
                      : 'No rastreado'}
                  </span>
                </div>
                {(supply.supplierIds || []).length > 0 && (
                  <div className="supply-info-row">
                    <span className="info-label">Proveedores:</span>
                    <span className="info-value">
                      {supply.supplierIds.length} proveedor{supply.supplierIds.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                )}
              </div>
              <div className="supply-card-actions">
                <Link to={`/admin/insumos/${supply.id}`}>
                  <Button variant="outline" size="small" style={{ width: '100%' }}>
                    Editar
                  </Button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default AdminSuppliesList

